import { NavBar } from 'components/NavBar/NavBar';
import { NoItems } from 'components/NoItems-fallback/NoItems';
import { clearFuelDashboard, clearFuelData, clearTanks, getFuelAlerts, getFuelConsumptionOverTime, getFuelDashboardTanks, getFuelTanks, getLastBatches, getLastDispenses } from 'features/fuelManagement/fuelManagementSlice';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { Box, Button, ColoredDot, Flex, MainHeadline, PageWrapper } from 'styling/GlobalStyles';
import { IBatch, IDispense, IFuelDashboardTank } from 'types/api/fuelManagementApi.interface';
import { formatDateForDisplay, formatNumbersForDisplay } from 'utils/helpers';
import { AddBatchModal } from './AddBatchModal';
import { SmallTable, SmallThead } from 'components/SmallTable/SmallTable.styled';
import { ConsumptionPeriodButton, FuelConsumptionPeridContainer, FuelDashboardRow, FuelTableContainer } from './FuelManagement.styled';
import { BodyText, OtherText, TitleText } from 'styling/Typography';
import { createPortal } from 'react-dom';
import { CircleChart } from 'components/Chart/CircleChart';
import { AreaChart } from 'components/Chart/AreaChart';
import useQueryPagination from 'hooks/useQueryPagination';
import { SelectTankModal } from './SelectTankModal';
import { fuelConsumptionPeriods, fuelAlertRiskLevels } from './fuelData';
import { parseISO, format, isValid } from 'date-fns';
import { FuelAlertsModal } from './FuelAlertsModal';
import Chevron from 'assets/icons/Chevron';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { PctBadge, StatusPill, TankCard, TankFill, TankInfo, TankMetricBlock, TankMetricLabel, TankMetricRow, TankMetricValue, TankName, TanksGrid, TankTopRow, TankVisual } from './Tanks/FuelTanks.styled';

const TANK_STATUS_COLORS = { ok: '#22C55E', watch: '#F97316', low: '#EF4444' }
const TANK_STATUS_LABELS = { ok: 'Ok', watch: 'Watch', low: 'Low' }
const getTankStatus = (current: number, capacity: number): 'ok' | 'watch' | 'low' => {
    const pct = capacity > 0 ? (current / capacity) * 100 : 0
    if (pct > 50) return 'ok'
    if (pct > 20) return 'watch'
    return 'low'
}

const spin = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

const WidgetLoader = styled.div`
    width: 42px;
    height: 42px;
    border: 4px solid rgba(255, 153, 0, 0.2);
    border-top-color: #ff9900;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

export const FuelManagement = () => {
    const {
        batches,
        dispenses,
        dashboardTanks,
        dashboardTanksLoading,
        fuelConsumption,
        alerts,
        isLoadingAlerts,
        tanks
    } = useSelector((state: RootState) => state.fuelManagement)
    const [batchesModal, setBatchesModal] = useState(false)
    const [notificationsModal, setNotificationsModal] = useState(false)
    const [selectedTank, setSelectedTank] = useState<{ openModal: boolean, tankId: number | null }>({ openModal: false, tankId: null })
    const [selectedPeriod, setSelectedPeriod] = useState(1)
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()

    useQueryPagination({
        fetchFunctions: [getFuelConsumptionOverTime, getLastBatches, getLastDispenses, getFuelDashboardTanks, getFuelAlerts],
        cleanUpFunctions: [clearFuelData, clearFuelDashboard],
        params: null,
        runInParallel: true
    })

    useEffect(() => {
        dispatch(getFuelTanks({ page: 1, limit: 4 }))
        return () => { dispatch(clearTanks()) }
    }, [])
    const data = useMemo(() => {
        if (!fuelConsumption?.points?.length) return [];

        return fuelConsumption.points.map(({ groupByKey, liters }) => {
            const date = parseISO(groupByKey);
            const safeValue = Number(liters) || 0;

            let name = '';

            switch (selectedPeriod) {
                case 1: // 12 Months → only month names
                    name = isValid(date) ? format(date, 'MMM') : groupByKey; // "Jan"
                    break;

                case 2: // 30 Days → only day number, no leading zero
                    if (isValid(date)) {
                        const day = format(date, 'd'); // '1', '2', '10', etc. (no leading 0)
                        name = day;
                    } else {
                        name = groupByKey;
                    }
                    break;

                case 3: // 7 Days → weekday names
                    name = isValid(date) ? format(date, 'EEE') : groupByKey; // "Mon"
                    break;

                case 4: // 24 Hours → hour only (no :00)
                    name = isValid(date) ? format(date, 'H') : groupByKey; // "0"–"23"
                    break;

                default:
                    name = groupByKey;
            }

            return {
                name,
                value: safeValue,
            };
        });
    }, [fuelConsumption, selectedPeriod]);

    function summarizeTanks(tanks: IFuelDashboardTank[]): { totalCapacity: number, totalAmount: number, percentage: number, sensorAmount: number, sensorPercentage: number } {
        if (!Array.isArray(tanks) || tanks.length === 0) {
            return { totalCapacity: 0, totalAmount: 0, percentage: 0, sensorAmount: 0, sensorPercentage: 0 };
        }

        const totals = tanks.reduce(
            (acc, tank) => {
                acc.totalCapacity += tank.capacity || 0;
                acc.totalAmount += tank.currentAmount || 0;
                return acc;
            },
            { totalCapacity: 0, totalAmount: 0 }
        );

        const percentage = totals.totalCapacity
            ? (totals.totalAmount / totals.totalCapacity) * 100
            : 0;

        const sensorPercentage = totals.totalCapacity
            ? ((dashboardTanks?.totalTankCurrentAmountBySensor || 0) / totals.totalCapacity) * 100
            : 0;

        return {
            totalCapacity: totals.totalCapacity,
            totalAmount: totals.totalAmount,
            sensorAmount: dashboardTanks?.totalTankCurrentAmountBySensor || 0,
            percentage: Number(percentage.toFixed(2)),
            sensorPercentage: Number(sensorPercentage.toFixed(2)),
        };
    }

    const tankValues: { totalCapacity: number, totalAmount: number, percentage: number, sensorAmount: number, sensorPercentage: number } = useMemo(() => {
        if (!selectedTank.tankId) {
            return summarizeTanks(dashboardTanks?.tanks || [])
        } else {
            const tank = dashboardTanks?.tanks.find(t => t.id == selectedTank.tankId)
            if (!tank) {
                return { totalCapacity: 0, totalAmount: 0, percentage: 0, sensorAmount: 0, sensorPercentage: 0 }
            }
            return { sensorAmount: tank?.sensorCurrentAmount, totalCapacity: tank?.capacity, totalAmount: tank.currentAmount, sensorPercentage: tank.capacity ? Number(((tank?.sensorCurrentAmount / tank.capacity) * 100).toFixed(2)) : 0, percentage: tank.capacity ? Number(((tank.currentAmount / tank.capacity) * 100).toFixed(2)) : 0 }
        }
    }, [selectedTank.tankId, dashboardTanks])



    const handleConsumption = (period: number) => {
        setSelectedPeriod(period)
        dispatch(getFuelConsumptionOverTime(period))
    }

    return <>
        <NavBar />
        <PageWrapper noFooter>
            <Flex between center mb='16' >
                <MainHeadline>Fuel Management</MainHeadline>
                <Flex gap='1rem' center >

                    {/* <Button width='150' orange onClick={() => navigate('fuel-tasks/add')}>
                        Add Fuel Task
                    </Button> */}
                    <Button width='150' orange onClick={() => setBatchesModal(true)}>
                        Add Batch
                    </Button>
                    {/* <Button width='150' orange onClick={() => setDispensesModal(true)} >
                        Add Dispense
                    </Button> */}
                    {/* <SquareIcon style={{ height: 40, borderRadius: 10, cursor: 'pointer' }} onClick={() => setNotificationsModal(true)} >
                        {FileIcon}
                    </SquareIcon> */}
                </Flex>

            </Flex>
            <Box width='100%' mb='1rem' >
                <Flex width='100%' between mb='16' >
                    <TitleText weight='bold' >FUEL STATUS</TitleText>
                    <OtherText onClick={() => navigate('/fuel/tanks')} style={{ color: '#ff9900', cursor: 'pointer' }} >View All</OtherText>
                </Flex>
                {tanks?.isLoading ? (
                    <Flex width='100%' justcenter center><WidgetLoader /></Flex>
                ) : !tanks?.data?.length ? (
                    <Flex width='100%' ><NoItems title='Fuel Tanks' /></Flex>
                ) : (
                    <TanksGrid>
                        {tanks.data.map((item) => {
                            const pct = item.capacity > 0 ? Math.round((item.currentAmount / item.capacity) * 100) : 0
                            const status = getTankStatus(item.currentAmount, item.capacity)
                            const color = TANK_STATUS_COLORS[status]
                            return (
                                <TankCard key={item.id} >
                                    <TankVisual>
                                        <TankFill h={pct} color={color} />
                                        <PctBadge h={pct} color={color}>{pct}%</PctBadge>
                                    </TankVisual>
                                    <TankInfo>
                                        <TankTopRow>
                                            <TankName>{item.name}</TankName>
                                            <StatusPill tone={status}>{TANK_STATUS_LABELS[status]}</StatusPill>
                                        </TankTopRow>
                                        <div>
                                            <TankMetricLabel>Current Amount</TankMetricLabel>
                                            <TankMetricValue>{formatNumbersForDisplay(item.currentAmount)}l</TankMetricValue>
                                        </div>
                                        <TankMetricRow>
                                            <TankMetricBlock>
                                                <TankMetricLabel>Capacity</TankMetricLabel>
                                                <TankMetricValue>{formatNumbersForDisplay(item.capacity)} L</TankMetricValue>
                                            </TankMetricBlock>
                                        </TankMetricRow>
                                    </TankInfo>
                                </TankCard>
                            )
                        })}
                    </TanksGrid>
                )}
            </Box>
            <FuelDashboardRow mb='1rem' >
                <Box height='280px' width='30%' stretchOn={1020} >
                    <Flex column between height='100%'>
                        <Flex width='100%' between >
                            <TitleText weight='bold'>FUEL STATUS</TitleText>
                            <Flex gap='0.5rem' onClick={() => setSelectedTank(prev => ({ tankId: prev.tankId, openModal: true }))} style={{ cursor: 'pointer' }} >
                                <BodyText weight='bold'  >{dashboardTanks?.tanks?.find(t => t.id == selectedTank.tankId)?.name || 'ALL TANKS'}</BodyText>
                                <Chevron />
                            </Flex>
                        </Flex>
                        {dashboardTanksLoading ? (
                            <Flex height='100%' width='100%' justcenter center><WidgetLoader /></Flex>
                        ) : (
                            <Flex column center between gap='1rem' >
                                <CircleChart fuel label={tankValues?.percentage.toString()} secondaryPercentage={tankValues?.sensorPercentage} percentage={tankValues?.percentage} />
                                <Flex column width='260px' gap='0.5rem' >
                                    <Flex width='100%' between >
                                        <Flex gap='1rem' center ><ColoredDot color={(tankValues?.percentage <= 10 ? '#CF1300' : tankValues?.percentage <= 20 ? '#8C6F00' : '#1B6F00')} /><BodyText weight='bold' >Total Fuel</BodyText></Flex>
                                        <BodyText weight='bold' >{formatNumbersForDisplay(tankValues?.totalAmount)} L / {formatNumbersForDisplay(tankValues?.totalCapacity)} L</BodyText>
                                    </Flex>
                                    <Flex width='100%' between>
                                        <Flex gap='1rem' center ><ColoredDot color='#ff9900' /><BodyText weight='bold' >Sensor Data: </BodyText></Flex>
                                        <BodyText weight='bold' >{formatNumbersForDisplay(tankValues?.sensorAmount || 0)} L / {formatNumbersForDisplay(tankValues?.totalCapacity || 0)} L</BodyText>
                                    </Flex>
                                </Flex>
                            </Flex>
                        )}
                    </Flex>
                </Box>

                <Box height='280px' width='40%' stretchOn={1020} >
                    <Flex column gap='1rem' between height='100%' style={{ paddingBottom: '1rem' }} >
                        <Flex width='100%' between >
                            <TitleText weight='bold'>CONSUMPTION OVER TIME</TitleText>
                            <OtherText style={{ color: '#ff9900', cursor: 'pointer', }} onClick={() => navigate('machines')} >  View Details </OtherText>
                        </Flex>
                        <FuelConsumptionPeridContainer>
                            {fuelConsumptionPeriods.map(p => <ConsumptionPeriodButton key={p.value} onClick={() => handleConsumption(p.value)} active={selectedPeriod == p.value} ><OtherText>{p.label}</OtherText></ConsumptionPeriodButton>)}
                        </FuelConsumptionPeridContainer>
                        {data.every(d => !d.value) ? <Flex height='150px' center justcenter ><OtherText>No data available</OtherText></Flex> : <AreaChart data={data} showLabels height={140} />}
                    </Flex>
                </Box>

                <Box height='280px' width='30%' stretchOn={1020} >
                    <Flex column gap='1rem' height='100%'>
                        <Flex width='100%' between>
                            <TitleText weight='bold'>ALERTS/ANOMALIES DETECTED</TitleText>
                            <OtherText onClick={() => setNotificationsModal(true)} style={{ color: '#ff9900', cursor: 'pointer', }}> View Details </OtherText>
                        </Flex>
                        <Flex height='100%' width='100%' justcenter center>
                            {isLoadingAlerts ? <WidgetLoader /> : <Flex style={{ paddingBottom: '0.5rem' }} column height='100%' between>
                                {
                                    alerts?.length ? alerts?.slice(0, 5)?.map(a => <Flex style={{ flex: 1 }} key={a.id} gap='0.8rem' center >
                                        <ColoredDot color='#ff9900' />
                                        <OtherText weight='bold'>{a.summary} · {fuelAlertRiskLevels[a.riskLevel]} · {a.machine?.name || 'No machine'}</OtherText>
                                    </Flex>) : <Flex width='100%' style={{ flex: 1 }} center justcenter ><OtherText>No Alerts For This Week</OtherText></Flex>
                                }
                            </Flex>}
                        </Flex>



                    </Flex>
                </Box>
            </FuelDashboardRow >
            <FuelDashboardRow >
                <FuelTableContainer>
                    <Flex width='100%' between >
                        <TitleText weight='bold' >FUEL BATCHES</TitleText>
                        <OtherText onClick={() => navigate('batches')} style={{ color: '#ff9900', cursor: 'pointer', }}> View More </OtherText>
                    </Flex>
                    {!batches?.data.length ? (
                        <Flex width='100%'  ><NoItems title='Batches' /></Flex>
                    ) : (
                        <Flex column width='100%' alignEnd gap='1rem'>
                            <SmallTable>
                                <SmallThead>
                                    <tr>
                                        <th>Amount</th>
                                        <th>Fuel Tank</th>
                                        <th>Fuel Type</th>
                                        <th>Supplier</th>
                                        <th>Date of purchase</th>
                                    </tr>
                                </SmallThead>

                                <tbody>
                                    {batches?.data &&
                                        batches.data.map((item: IBatch) => {
                                            return (
                                                <tr key={item.id || '/'} >
                                                    <td>{formatNumbersForDisplay(item.quantity) || '/'}</td>
                                                    <td>{item?.fuelTank?.name || '/'}</td>
                                                    <td>{item?.fuelType.name || '/'}</td>
                                                    <td>{item?.supplier?.name || '/'}</td>
                                                    <td>{formatDateForDisplay(item.date, true)}</td>
                                                </tr>
                                            )
                                        })}
                                </tbody>
                            </SmallTable>

                        </Flex>
                    )}
                </FuelTableContainer>
                <FuelTableContainer>
                    <Flex width='100%' between >
                        <TitleText weight='bold' >FUEL DISPENSES</TitleText>
                        <OtherText onClick={() => navigate('dispenses')} style={{ color: '#ff9900', cursor: 'pointer' }}> View More </OtherText>
                    </Flex>
                    {!dispenses?.data.length ? (
                        <Flex width='100%' ><NoItems title='Dispenses' /></Flex>
                    ) : (
                        <Flex width='100%' alignEnd column gap='1rem' >
                            <SmallTable>
                                <SmallThead>
                                    <tr>
                                        <th>Amount</th>
                                        <th>Operator</th>
                                        <th>From Tank</th>
                                        <th>Machine</th>
                                        <th>Date</th>
                                    </tr>
                                </SmallThead>
                                <tbody>
                                    {dispenses?.data &&
                                        dispenses.data.map((item: IDispense) => {
                                            return (
                                                <tr key={item.id} >
                                                    <td>{formatNumbersForDisplay(item?.quantity) || '/'}</td>
                                                    <td>{item?.user?.firstName + ' ' + item?.user?.lastName}</td>
                                                    <td>{item?.fromFuelTank?.name || '/'}</td>
                                                    <td>{item.machine?.name || '/'}</td>
                                                    <td>{formatDateForDisplay(item.dispensedAt, true)}</td>
                                                </tr>
                                            )
                                        })}
                                </tbody>
                            </SmallTable>
                        </Flex>
                    )}
                </FuelTableContainer>
            </FuelDashboardRow>
            {batchesModal && <AddBatchModal closeModal={() => setBatchesModal(false)} />}
            {selectedTank.openModal && <SelectTankModal selectedTank={selectedTank.tankId} closeModal={() => setSelectedTank(prev => ({ ...prev, openModal: false }))} tankList={dashboardTanks?.tanks} selectTank={(id) => setSelectedTank({ openModal: true, tankId: id })} />}
            {/* {dispensesModal && <AddDispenseModal closeModal={() => setDispensesModal(false)} />} */}
            {notificationsModal && createPortal(<FuelAlertsModal closeModal={() => setNotificationsModal(false)} />, document.body)}

        </PageWrapper >

    </>
}
